/**
 * App theme adapter — same keys/events as QMF settings-float / auth-widget.
 * Does not replace QMF; writes `qmf-theme` + `data-theme`.
 */

const THEME_KEYS = ['qmf-theme', 'lab-theme'];

/** @returns {'light'|'dark'} */
export function getTheme() {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  try {
    for (const key of THEME_KEYS) {
      const stored = localStorage.getItem(key);
      if (stored === 'dark' || stored === 'light') return stored;
    }
  } catch {
    /* ignore */
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Apply theme to the document without emitting events.
 * @param {'light'|'dark'|string} theme
 * @returns {'light'|'dark'}
 */
export function applyTheme(theme) {
  const next = theme === 'dark' ? 'dark' : 'light';
  const root = document.documentElement;
  root.setAttribute('data-theme', next);
  try {
    root.style.colorScheme = next;
  } catch {
    /* ignore */
  }
  try {
    for (const key of THEME_KEYS) localStorage.setItem(key, next);
  } catch {
    /* ignore */
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', next === 'dark' ? '#0f172a' : '#3F5EFB');
  const splash = document.getElementById('qm-initial-loading');
  if (splash) splash.classList.toggle('qm-load--dark', next === 'dark');
  window.__qeTheme = next;
  try {
    window.QmfI18n?.setTheme?.(next, { emit: false });
  } catch {
    /* ignore */
  }
  return next;
}

/**
 * @param {'light'|'dark'|string} theme
 * @returns {'light'|'dark'}
 */
export function setTheme(theme) {
  const next = applyTheme(theme);
  window.dispatchEvent(new CustomEvent('qmf:themechange', { detail: { theme: next } }));
  window.dispatchEvent(
    new CustomEvent('qmf:prefs-change', { detail: { theme: next } }),
  );
  return next;
}

export function toggleTheme() {
  return setTheme(getTheme() === 'dark' ? 'light' : 'dark');
}

/**
 * @param {(theme: 'light'|'dark') => void} fn
 * @returns {() => void}
 */
export function onThemeChange(fn) {
  const onCustom = (e) => fn(e.detail?.theme === 'dark' ? 'dark' : getTheme());
  const onStorage = (e) => {
    if (e.key && THEME_KEYS.includes(e.key)) {
      const next = applyTheme(e.newValue === 'dark' ? 'dark' : 'light');
      fn(next);
    }
  };
  window.addEventListener('qmf:themechange', onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('qmf:themechange', onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

export function initTheme() {
  applyTheme(getTheme());
  const sync = (theme) => applyTheme(theme === 'dark' ? 'dark' : 'light');
  window.addEventListener('qmf:themechange', (e) => {
    sync(e.detail?.theme ?? getTheme());
  });
  window.addEventListener('qmf:prefs-change', (e) => {
    if (e.detail?.theme) sync(e.detail.theme);
  });
  window.addEventListener('storage', (e) => {
    if (e.key && THEME_KEYS.includes(e.key)) sync(e.newValue);
  });
}
